import { hasOwn } from './utils';
import functionParser from '@ali/vu-function-parser';

const R_EXPR = /\${.+}/;
function isExpr(str) {
  return R_EXPR.test(str);
}
function cloneDeep(src) {
  const type = typeof src;

  let data;
  let hash;
  if (src == null) {
    hash = 'null';
    data = src;
  } else if (Array.isArray(src)) {
    hash = [];
    data = src.map((item) => {
      const ret = cloneDeep(item);
      hash.push(ret.hash);
      return ret.data;
    });
    hash = `[${hash.join(',')}]`;
  } else if (type === 'object') {
    data = {};
    hash = [];
    Object.keys(src).sort().forEach((key) => {
      const ret = cloneDeep(src[key]);
      data[key] = ret.data;
      hash.push(`${key}:${ret.hash}`);
    });
    hash = `{${hash.join(',')}}`;
  } else if (type === 'string') {
    hash = `"${src}"`;
    data = src;
  } else {
    hash = String(src);
    data = src;
  }

  return { data, hash };
}

// 这个递归的 new Data 需要优化
// 比如我一个变量没值，报了 16 次错误 ‘datapool_var_6 is not defined’
class Data {
  constructor(owner, parent, data, key) {
    this.type = 'RAW';
    this.parent = parent;
    this.exprCount = 0;
    this.data = data;
    this.key = key;
    // 这里 data 可能就是 false, 有可能有问题吧？
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      this.type = 'LIST';
      this.items = data.map((child, index) => {
        const item = new Data(owner, this, child, `${index}`);
        if (item.hasExpr()) {
          this.exprCount++;
        }
        if (item.isModified()) {
          this.modified = true;
        }
        return item;
      });
      return;
    }

    const t = typeof data;
    if (t === 'object') {
      if (data.type === 'variable') {
        if (data.variable != null && data.variable !== '') {
          this.type = 'EXPR';
          this.exprCount = 1;
          this.expr = data.variable;
          this.__mergeFromGlobal = data.__mergeFromGlobal;
          owner.addExprPoint(this);
        } else {
          this.data = data.value;
          this.modified = true;
        }
        return;
      }
      this.type = 'MAP';
      if ((data.compiled && data.source) || data.type === 'actionRef') {
        const path = [this.key];
        let { parent: _parent } = this;
        let needAdd = true;
        while (_parent && _parent.key) {
          if (_parent.key === 'events') {
            // 已经解析过了，无视它
            needAdd = false;
            break;
          }
          path.push(_parent.key);
          _parent = _parent.parent;
        }
        if (needAdd) {
          if (data.type === 'actionRef') {
            this.type = 'actionRef';
            this.expr = data;
            owner.addFuncPoint(this, path.reverse());
            delete this.expr;
          } else {
            this.type = 'JS';
            this.expr = data.compiled;
            owner.addFuncPoint(this, path.reverse());
          }
        } else if (data.compiled) {
          data = data.compiled;
        }
      } else if (data.type === 'js') {
        // fix: situation when there is empty titleRender but there is title
        // over write parent data
        data = data.source;
        if (this.parent.data && (key in this.parent.data)) {
          this.parent.data[key] = data;
        }
        this.data = data;
        this.type = 'RAW';
      }
      if (this.type === 'MAP') {
        this.entries = Object.keys(data);
        this.items = this.entries.map((_key) => {
          const item = new Data(owner, this, data[_key], _key);
          if (item.hasExpr()) {
            this.exprCount++;
          }
          if (item.isModified()) {
            this.modified = true;
          }
          return item;
        });
      }
      return;
    }

    if (t === 'string' && isExpr(data)) {
      this.type = 'EXPR';
      this.exprCount = 1;
      this.expr = data;
      owner.addExprPoint(this);
    }
  }

  hasExpr() {
    return this.exprCount > 0;
  }

  isModified() {
    return this.modified;
  }

  cacheData() {
    if (this.type === 'LIST') {
      this.data = this.items.map(item => item.getData());
    } else if (this.type === 'MAP') {
      this.data = {};
      this.entries.forEach((key, index) => {
        this.data[key] = this.items[index].getData();
      });
    }
  }

  getData() {
    if (this.modified) {
      this.cacheData();
      this.modified = false;
    }
    return this.data;
  }

  statChange() {
    if (this.modified) return;
    this.modified = true;
    if (this.parent) {
      this.parent.statChange();
    }
  }

  parseInto(store, appendVariables, scope, xprops) {
    // clear usedTokens every run
    this.usedTokens = {};
    const { data, hash } = cloneDeep(store.parse(
      this.expr,
      appendVariables,
      this.usedTokens,
      scope,
      xprops
    ));
    if (hash === this.hash) {
      return false;
    }
    this.hash = hash;
    this.data = data;
    // merge global
    if (Array.isArray(this.__mergeFromGlobal)) {
      this.data = [...this.__mergeFromGlobal, ...(this.data || [])];
    }
    this.statChange();
    return true;
  }
}

class DataEntry {
  constructor(owner, data, key) {
    this.data = new Data(owner, this, data, key);
    if (!this.data.hasExpr() && !this.data.isModified()) {
      this.rawData = data;
      this._cached = true;
    }
    this.key = key;
    this._status = 'DEFAULT';
  }

  statChange() {
    this._cached = false;
    this._status = 'CHANGED';
  }

  get empty() {
    return this.value == null;
  }

  get status() {
    return this._status;
  }

  get value() {
    if (!this._cached) {
      this.rawData = this.data.getData();
      this._cached = true;
    }
    return this.rawData;
  }

  savePoint() {
    this._status = 'DEFAULT';
  }
}

export default class HotProps {
  constructor(store, propsTemplate, scope) {
    this.entries = Object.keys(propsTemplate);
    this.store = store;
    const maps = {};
    this.expressionsHeap = [];
    this.funcHeap = [];
    this.scope = scope;
    this.entries.forEach((key) => {
      maps[key] = new DataEntry(this, propsTemplate[key], key);
    });
    this.maps = maps;
    this.cacheRaw();
  }

  /**
   * separate hotprops in to two phases, so state could be initialized before expression parse.
   * @param {Xprops} xprops xprops this hotprops belongs
   * @param {Object} usedTokens used variable name will be collected in
   */
  parseExpr(xprops, usedTokens, appendVariables) {
    if (this.hasExpression()) {
      const { store, scope } = this;
      this.expressionsHeap.forEach((item) => {
        item.parseInto(store, appendVariables, scope, xprops);
        (Object.keys(item.usedTokens || {})).forEach((token) => {
          usedTokens[token] = true;
        });
      });
    }

    this.cacheRaw();
  }

  addExprPoint(point) {
    this.expressionsHeap.push(point);
  }

  addFuncPoint(point, path) {
    // generate path
    this.funcHeap.push({
      expr: functionParser(point.expr),
      path,
    });
  }

  hasExpression() {
    return this.expressionsHeap.length > 0;
  }

  getExpression() {
    return this.expressionsHeap;
  }

  hasFunction() {
    return this.funcHeap.length > 0;
  }

  getFunction() {
    return this.funcHeap;
  }

  get(name) {
    if (!hasOwn(this.maps, name)) {
      return new DataEntry(this, null, name);
    }
    return this.maps[name];
  }

  getValue(name) {
    return this.get(name).value;
  }

  got(name) {
    const v = this.get(name).value;
    return !!v && v != ''; // eslint-disable-line
  }

  cacheRaw() {
    const maps = {};
    this.entries.forEach((key) => {
      maps[key] = this.maps[key].value;
    });
    this.raw = maps;
  }

  toJS() {
    return this.raw;
  }

  getEntries() {
    return this.entries;
  }

  update(appendVariables, scope, xprops) {
    this.savePoint();
    let changed = false;
    this.expressionsHeap.forEach((item) => {
      if (item.parseInto(this.store, appendVariables, scope, xprops)) {
        changed = true;
      }
    });
    if (changed) {
      this.cacheRaw();
    }

    return changed;
  }

  savePoint() {
    this.entries.forEach((key) => {
      const item = this.maps[key];
      if (item.status === 'CHANGED') {
        item.savePoint();
      }
    });
  }
}
